/**
 * StreamClient — typed wrapper over the OpenMarkets Flow WebSocket.
 *
 * Protocol (docs/API-NOTES.md "WebSocket protocol"):
 *  - connect to `wss://…/stream?api_key=…` (key rides in the query string)
 *  - server greets with `{type:"connected"}`, then `{type:"subscribed"}`,
 *    `{type:"liquidity.update"}`, contest-state updates, `{type:"pong"}`,
 *    `{type:"error"}`
 *  - re-subscribing REPLACES the filter list for a channel (max 200 contests)
 *  - server sends protocol pings every ~25s; `ws` auto-pongs, but we keep a 60s
 *    liveness timer and terminate+reconnect if the socket goes quiet
 *
 * Features: internal desired-subscription state (so reconnects resubscribe),
 * auto-reconnect with exponential backoff (capped), and a clean `close()`.
 */
import WebSocket from "ws";
const DEFAULT_WS_URL = "wss://api.openmarkets.ai/flow/v1/stream";
export class StreamClient {
    apiKey;
    baseUrl;
    WS;
    maxReconnectDelayMs;
    reconnectBaseMs;
    livenessTimeoutMs;
    ws = null;
    closedByUser = false;
    reconnectAttempt = 0;
    reconnectTimer = null;
    livenessTimer = null;
    /** Desired subscriptions per channel; the source of truth across reconnects. */
    desired = new Map();
    /** Whether we want the (own-account) orders channel; re-sent on reconnect. */
    desiredOrders = false;
    /** Desired arbitrage-channel filters (null = not subscribed); re-sent on reconnect. */
    desiredArbitrage = null;
    listeners = new Map();
    constructor(opts) {
        if (!opts.apiKey)
            throw new Error("StreamClient requires an apiKey");
        this.apiKey = opts.apiKey;
        this.baseUrl = opts.url ?? DEFAULT_WS_URL;
        this.WS = opts.WebSocketImpl ?? WebSocket;
        this.maxReconnectDelayMs = opts.maxReconnectDelayMs ?? 30_000;
        this.reconnectBaseMs = opts.reconnectBaseMs ?? 1_000;
        this.livenessTimeoutMs = opts.livenessTimeoutMs ?? 60_000;
    }
    /* ------------------------------------------------------------- listeners */
    on(event, cb) {
        let set = this.listeners.get(event);
        if (!set) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(cb);
        return this;
    }
    off(event, cb) {
        this.listeners.get(event)?.delete(cb);
        return this;
    }
    emit(event, ...args) {
        const set = this.listeners.get(event);
        if (!set)
            return;
        for (const cb of set) {
            try {
                cb(...args);
            }
            catch {
                // A misbehaving listener must not take down the stream.
            }
        }
    }
    /* ----------------------------------------------------------- connection */
    connect() {
        this.closedByUser = false;
        this.open();
        return this;
    }
    get connected() {
        return this.ws !== null && this.ws.readyState === this.WS.OPEN;
    }
    open() {
        if (this.ws)
            return;
        const url = `${this.baseUrl}?api_key=${encodeURIComponent(this.apiKey)}`;
        const ws = new this.WS(url);
        this.ws = ws;
        ws.on("open", () => {
            this.reconnectAttempt = 0;
            this.resetLiveness();
            this.emit("open");
            this.resubscribeAll();
        });
        ws.on("message", (data) => {
            this.resetLiveness();
            this.handleMessage(data);
        });
        ws.on("ping", () => this.resetLiveness());
        ws.on("pong", () => this.resetLiveness());
        ws.on("error", (err) => this.emit("error", err));
        ws.on("close", (code, reason) => {
            this.cleanupSocket();
            this.emit("close", { code, reason: reason?.toString() ?? "" });
            if (!this.closedByUser)
                this.scheduleReconnect();
        });
    }
    handleMessage(data) {
        let msg;
        try {
            msg = JSON.parse(data.toString());
        }
        catch {
            return; // ignore non-JSON frames
        }
        this.emit("message", msg);
        if (!msg || typeof msg !== "object")
            return;
        const type = msg.type;
        if (typeof type !== "string")
            return;
        switch (type) {
            case "connected":
                this.emit("connected", msg);
                break;
            case "subscribed":
                this.emit("subscribed", msg);
                break;
            case "liquidity.update":
                this.emit("liquidity.update", msg);
                break;
            case "pong":
                this.emit("pong", msg);
                break;
            case "error":
                this.emit("error", msg);
                break;
            default:
                // Account-scoped orders channel: order.update / order.settled (#667).
                if (type.startsWith("order.")) {
                    this.emit("order", msg);
                }
                else if (type.startsWith("arbitrage.")) {
                    this.emit("arbitrage.update", msg);
                }
                // Contest-state push types are undocumented; route anything that looks
                // like one to the contest_state event (see FRICTION.md).
                else if (type.startsWith("contest_state")) {
                    this.emit("contest_state", msg);
                }
                break;
        }
    }
    send(obj) {
        if (this.ws && this.ws.readyState === this.WS.OPEN) {
            this.ws.send(JSON.stringify(obj));
            return true;
        }
        return false;
    }
    /* --------------------------------------------------------- subscriptions */
    /** Add contests to a channel's subscription and push the new filter upstream. */
    subscribe(channel, contestIds) {
        let set = this.desired.get(channel);
        if (!set) {
            set = new Set();
            this.desired.set(channel, set);
        }
        for (const id of contestIds)
            set.add(id);
        // Re-subscribing replaces the server-side filter, so send the full set.
        this.send({ action: "subscribe", channel, contest_ids: [...set] });
    }
    /** Remove contests from a channel; resend the reduced filter (or unsubscribe). */
    unsubscribe(channel, contestIds) {
        const set = this.desired.get(channel);
        if (!set)
            return;
        for (const id of contestIds)
            set.delete(id);
        if (set.size > 0) {
            this.send({ action: "subscribe", channel, contest_ids: [...set] });
        }
        else {
            this.desired.delete(channel);
            this.send({ action: "unsubscribe", channel, contest_ids: contestIds });
        }
    }
    /**
     * Subscribe your OWN account's `orders` channel (order.update / order.settled, #667).
     * Send NO `account_ids` — the server scopes a bare orders subscribe to the key's own
     * account (verified 2026-07-24). Naming `account_ids` (even your own) is treated as a
     * cross-account scope and rejected `entitlement_required` unless the key has
     * `connect:trade`. Tracked so a reconnect re-subscribes automatically. See FRICTION.md.
     */
    subscribeOrders() {
        this.desiredOrders = true;
        this.send({ action: "subscribe", channel: "orders" });
    }
    /** Stop receiving order events. */
    unsubscribeOrders() {
        this.desiredOrders = false;
        this.send({ action: "unsubscribe", channel: "orders" });
    }
    /**
     * Subscribe the `arbitrage` channel (needs `arbitrage:read`). Pushes `arbitrage.update`
     * per contest. Filters narrow the feed server-side; re-subscribing replaces them, and
     * they're tracked so a reconnect re-subscribes automatically.
     */
    subscribeArbitrage(filters = {}) {
        this.desiredArbitrage = { ...filters };
        this.send({ action: "subscribe", channel: "arbitrage", ...filters });
    }
    /** Stop receiving arbitrage events. */
    unsubscribeArbitrage() {
        this.desiredArbitrage = null;
        this.send({ action: "unsubscribe", channel: "arbitrage" });
    }
    /** Explicit application-level ping (`{action:"ping"}` → server `pong`). */
    ping() {
        this.send({ action: "ping" });
    }
    resubscribeAll() {
        for (const [channel, set] of this.desired) {
            if (set.size > 0) {
                this.send({ action: "subscribe", channel, contest_ids: [...set] });
            }
        }
        if (this.desiredOrders) {
            this.send({ action: "subscribe", channel: "orders" });
        }
        if (this.desiredArbitrage) {
            this.send({ action: "subscribe", channel: "arbitrage", ...this.desiredArbitrage });
        }
    }
    /* -------------------------------------------------- liveness & reconnect */
    resetLiveness() {
        if (this.livenessTimer)
            clearTimeout(this.livenessTimer);
        this.livenessTimer = setTimeout(() => {
            // No ping/message within the window → assume a dead socket and recycle it.
            const ws = this.ws;
            if (ws) {
                try {
                    ws.terminate();
                }
                catch {
                    /* ignore */
                }
            }
        }, this.livenessTimeoutMs);
        this.unref(this.livenessTimer);
    }
    scheduleReconnect() {
        if (this.closedByUser)
            return;
        const attempt = this.reconnectAttempt++;
        const cap = Math.min(this.maxReconnectDelayMs, this.reconnectBaseMs * 2 ** attempt);
        // Jitter within [cap/2, cap] to spread reconnect storms without hammering.
        const delayMs = Math.floor(cap / 2 + Math.random() * (cap / 2));
        this.emit("reconnecting", { attempt: attempt + 1, delayMs });
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (!this.closedByUser)
                this.open();
        }, delayMs);
        this.unref(this.reconnectTimer);
    }
    cleanupSocket() {
        if (this.livenessTimer) {
            clearTimeout(this.livenessTimer);
            this.livenessTimer = null;
        }
        if (this.ws) {
            this.disposeSocket(this.ws);
            this.ws = null;
        }
    }
    /**
     * Detach our listeners and dispose a socket without risking an unhandled 'error'.
     * Closing a socket that is still CONNECTING makes `ws` emit an async 'error'
     * ("WebSocket was closed before the connection was established"). Because we've
     * already removed our own listeners, that event would have no handler — Node then
     * treats it as an unhandled 'error' and crashes the process (this took down :8787
     * when the relay's idle timeout closed the upstream mid-connect). Keep a no-op
     * error handler attached, and forcibly `terminate()` a not-yet-open socket.
     */
    disposeSocket(ws) {
        ws.removeAllListeners();
        ws.on("error", () => { });
        try {
            if (ws.readyState === this.WS.CONNECTING)
                ws.terminate();
            else
                ws.close();
        }
        catch {
            /* ignore */
        }
    }
    /** Permanently close the connection; no further reconnects. */
    close() {
        this.closedByUser = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.livenessTimer) {
            clearTimeout(this.livenessTimer);
            this.livenessTimer = null;
        }
        const ws = this.ws;
        this.ws = null;
        if (ws)
            this.disposeSocket(ws);
    }
    unref(timer) {
        const t = timer;
        if (typeof t.unref === "function")
            t.unref();
    }
}
//# sourceMappingURL=stream.js.map